import { useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

/**
 * Merge-writes to the URL query string (DESIGN.md §3.7). Every deep-linked piece of state
 * (filters, an open record) goes through here so two writers never clobber each other.
 * Panels push history; filters, tabs and closing replace.
 */
export interface UrlParamsSetOptions {
  /** `replace` (default) rewrites the current entry; `push` adds one (opening a panel or viewer). */
  history?: 'replace' | 'push';
}

/** Applies `patch` to `current`: null or "" deletes the key, everything else is set; unrelated keys survive. */
export function mergeQuery(current: string, patch: Record<string, string | null>): string {
  const params = new URLSearchParams(current);
  for (const [key, value] of Object.entries(patch)) {
    if (value === null || value === '') params.delete(key);
    else params.set(key, value);
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useUrlParams() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const current = searchParams.toString();
  // Read at call time, not captured, so a stale closure cannot drop a key another writer just put
  // in the URL; each write also advances it, so two writes in one tick compose instead of clobbering.
  const currentRef = useRef(current);
  currentRef.current = current;
  const pathRef = useRef(pathname);
  pathRef.current = pathname;

  const get = useCallback((key: string) => searchParams.get(key), [searchParams]);

  const set = useCallback(
    (patch: Record<string, string | null>, opts: UrlParamsSetOptions = {}) => {
      const search = mergeQuery(currentRef.current, patch);
      currentRef.current = search.replace(/^\?/, '');
      navigate({ pathname: pathRef.current, search }, { replace: opts.history !== 'push' });
    },
    [navigate],
  );

  // The href `set(patch)` would navigate to, for a row's real link that opens a panel.
  const href = useCallback(
    (patch: Record<string, string | null>) => `${pathname}${mergeQuery(current, patch)}`,
    [pathname, current],
  );

  return useMemo(() => ({ get, set, href }), [get, set, href]);
}
